import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { BoltIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/catalyst/button';
import { isBackfillEnabledForUser } from '@/lib/featureFlags';
import {
  useActivateFirmServicesMutation,
  useExecuteFirmServicesSyncMutation,
  useGetFirmServicesStatusQuery,
  useLazyPreviewFirmServicesSyncQuery,
} from '@/services/api/firmServicesActivationService';
import { selectEmail } from '@/state/selectors/root-selectors';
import { getApiErrorMessage } from '@/utils/api-error';

interface FirmServicesStatusCardProps {
  firmId: string;
}

export const FirmServicesStatusCard: React.FC<FirmServicesStatusCardProps> = ({
  firmId,
}) => {
  const [showSyncConfirmDialog, setShowSyncConfirmDialog] = useState(false);
  const userEmail = useSelector(selectEmail);
  const showApplyToExistingCases = useMemo(
    () => isBackfillEnabledForUser(userEmail),
    [userEmail],
  );

  // Query for activation status
  const { data: statusData, isLoading: isLoadingStatus } =
    useGetFirmServicesStatusQuery({ firmId });

  // Mutations
  const [activateFirmServices, { isLoading: isActivating }] =
    useActivateFirmServicesMutation();

  // Lazy query for sync preview - fetch when activated
  const [triggerPreview, { data: previewData, isFetching: isFetchingPreview }] =
    useLazyPreviewFirmServicesSyncQuery();

  const [executeSync, { isLoading: isExecutingSync }] =
    useExecuteFirmServicesSyncMutation();

  const isActivated = statusData?.is_activated ?? false;
  const mattersCount = previewData?.matters_count ?? 0;

  // Fetch preview data when firm is activated
  useEffect(() => {
    if (isActivated && !previewData) {
      triggerPreview({ firmId });
    }
  }, [isActivated, firmId, previewData, triggerPreview]);

  const handleActivate = async () => {
    try {
      const result = await activateFirmServices({ firmId }).unwrap();
      if (result.already_active) {
        toast.info('Services already activated for this firm');
      } else {
        toast.success('Services activated successfully');
      }
    } catch (error) {
      toast.error(`Failed to activate services: ${getApiErrorMessage(error)}`);
    }
  };

  const handleExecuteSync = async () => {
    setShowSyncConfirmDialog(false);
    try {
      const result = await executeSync({ firmId }).unwrap();
      toast.success(
        `Applying scope of work to ${result.matters_count} existing cases. This may take a few minutes.`,
      );
    } catch (error) {
      toast.error(`Failed to start sync: ${getApiErrorMessage(error)}`);
    }
  };

  if (isLoadingStatus) {
    return (
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 p-4">
        <div className="animate-pulse flex items-center gap-3">
          <div className="h-5 w-5 bg-zinc-200 dark:bg-zinc-700 rounded" />
          <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-48" />
        </div>
      </div>
    );
  }

  // NOT ACTIVATED state
  if (!isActivated) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <BoltIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Ready to go live
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Activating enables scope-of-work-based task generation for all
                new cases. You can configure services below before activating.
              </p>
            </div>
          </div>
          <Button
            color="amber"
            onClick={handleActivate}
            disabled={isActivating}
            className="flex-shrink-0"
          >
            {isActivating ? 'Activating...' : 'Activate'}
          </Button>
        </div>
      </div>
    );
  }

  // ACTIVATED state
  return (
    <>
      <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Active
              </h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                Changes below will apply to new cases automatically.
                {showApplyToExistingCases &&
                  ' To retroactively update existing cases, click "Apply to Existing Cases".'}
              </p>
            </div>
          </div>
          {/* Secondary action - opens modal where primary action lives */}
          {showApplyToExistingCases && (
            <Button
              outline
              onClick={() => setShowSyncConfirmDialog(true)}
              disabled={isExecutingSync || isFetchingPreview}
              className="flex-shrink-0 text-xs"
            >
              {isFetchingPreview ? 'Loading...' : 'Apply to Existing Cases'}
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Dialog - serves as the preview */}
      <AlertDialog
        open={showSyncConfirmDialog}
        onOpenChange={setShowSyncConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply scope of work to existing cases
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will retroactively apply the current scope of work
                  configuration to{' '}
                  <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                    {mattersCount} existing cases
                  </span>
                  .
                </p>
                <ul className="list-disc list-inside text-sm text-zinc-500 dark:text-zinc-400 space-y-1">
                  <li>Matter-specific notes will be preserved</li>
                  <li>
                    Excludes:{' '}
                    {previewData?.exclude_statuses?.join(', ') ||
                      'Hidden, Complete, Client Dropped, To Be Dropped'}
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isExecutingSync}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecuteSync}
              disabled={isExecutingSync || mattersCount === 0}
            >
              {isExecutingSync
                ? 'Applying...'
                : mattersCount === 0
                  ? 'No cases to apply'
                  : `Apply to ${mattersCount} cases`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
